import React from 'react';
import { motion } from 'framer-motion';
import {
  ShieldOff,
  Activity,
  Wrench,
  Sun,
  FlaskConical,
  Sprout,
} from 'lucide-react';
import SectionHead from './SectionHead';
import { EASE } from './reveal';

const ADVANTAGES = [
  {
    icon: ShieldOff,
    title: 'Corrosion Resistance',
    body:
      'HDPE does not rust or rot — a critical advantage in saltwater environments where steel and aluminum slowly fail.',
  },
  {
    icon: Activity,
    title: 'Impact Tolerance',
    body:
      'It flexes and absorbs shock instead of denting or cracking, dramatically reducing damage from bumps and collisions.',
  },
  {
    icon: Wrench,
    title: 'Lower Maintenance',
    body:
      'No painting, no anti-fouling coatings, no constant repairs. Spend more time on the job and less time on upkeep.',
  },
  {
    icon: Sun,
    title: 'UV Durability',
    body:
      'Marine-grade HDPE is formulated to withstand years of relentless sun exposure without breaking down.',
  },
  {
    icon: FlaskConical,
    title: 'Chemical Resistance',
    body:
      'Stands up to oils, fuels, solvents, and the harsh contaminants common around working marine sites.',
  },
  {
    icon: Sprout,
    title: 'Marine Growth Resistance',
    body:
      'Its smooth, non-porous surface makes it hard for barnacles, algae, and other growth to take hold.',
  },
];

export default function WhyHDPE() {
  return (
    <section
      id="why-hdpe"
      className="relative py-16 sm:py-20 lg:py-32 bg-black border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
      <div className="absolute -top-24 -right-16 w-[320px] sm:w-[520px] h-[320px] sm:h-[520px] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-10 w-[280px] sm:w-[420px] h-[280px] sm:h-[420px] rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <SectionHead
          eyebrow="The Best Material for Barges"
          titleLines={[
            'Why HDPE?',
            <span key="accent" className="text-cyan-300">Maximum Uptime.</span>,
          ]}
          body="Our High-Density Polyethylene work barges are purpose-built for the marine environment. They never rust, rot, or oxidize, and our premium construction will ensure that your boats stay on the water and working as long as possible. HDPE outperforms steel, aluminum, and wood in six critical ways:"
        />

        <div className="mt-10 sm:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {ADVANTAGES.map((a, idx) => {
            const Icon = a.icon;
            const num = String(idx + 1).padStart(2, '0');
            return (
              <motion.div
                key={a.title}
                className="panel rounded-xl p-6 sm:p-7 relative group"
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, ease: EASE, delay: idx * 0.07 }}
              >
                <span className="absolute top-4 right-4 font-tech text-[10px] tracking-[0.28em] text-zinc-600 group-hover:text-cyan-400/70 transition-colors">
                  {num}
                </span>
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-md bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center text-cyan-300 mb-4 sm:mb-5">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-white text-base sm:text-lg font-semibold">
                  {a.title}
                </h3>
                <p className="mt-2 sm:mt-2.5 text-zinc-400 text-sm leading-relaxed">
                  {a.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
